// File: frontend/src/components/marketing/partners/PartnersHero.js
//
// The /ambassador hero: the offer in one sentence, the state-correct call to
// action, and an interactive, clearly illustrative referral journey:
//
//   Your referral link → Business joins PesaFii → Business generates
//   qualifying revenue → You earn <rate from the API>
//
// ── WHAT MOVES, AND WHY IT IS SAFE ──────────────────────────────────────────
//
// The headline, lede and CTAs never animate. The headline is the page's
// largest paint; fading it in would only delay it.
//
// The journey renders FINISHED by default: every card visible, every connector
// drawn, the commission card active. With motion allowed, one timeline then
// plays once: the link card activates, a connector travels to the business,
// the revenue node lights up and its commission slice grows, the last
// connector travels and the commission card reveals with a single light sweep.
// After that it settles into a gentle ambient drift of the background network
// (paused off-screen and on hidden tabs; skipped on low-power devices), and on
// fine-pointer desktops the network follows the cursor by a few pixels. There
// is no endless replay; "Replay" is the reader's choice.
//
// No count-up on the rate, deliberately (see PESAFII_AMBASSADORS_STATE.md):
// a counter shows a sequence of wrong rates on the way to the right one. The
// commission value reveals by scale, never by ticking through numbers.
//
// Every card is a button. Selecting one walks the path to that point, so the
// sequence can be explored by touch, mouse or keyboard, with or without motion.

import React, { useRef, useState } from "react";
import { Box, Container, Stack, Typography, Button, alpha, useTheme } from "@mui/material";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

import { gsap, useGSAP, ScrollTrigger, DUR, EASE, MQ, prefersLightweightMotion } from "../../../utils/gsapCore";
import { referralUrl } from "../../../config/origins";
import { PAGE_MQ } from "./partnersMotion";
import { CtaGroup, MONO } from "./PartnersPrimitives";

const FINAL = 3;
const SAMPLE_LINK = referralUrl("XXXX").replace(/^https?:\/\//, "");

// Grid gaps. The connectors are sized to these exactly, so keep them together.
const GAP = { col: 48, row: 40, stack: 28 };

// ═══════════════════════════════════════════════════════════════════════════
// HERO
// ═══════════════════════════════════════════════════════════════════════════

export function PartnersHero({ ratePct, cta, tierSummary }) {
  const rateText = ratePct != null ? `${ratePct}%` : null;

  const facts = [
    rateText ? `${rateText} recurring commission` : "Recurring commission",
    tierSummary,
    "No extra cost to the business you refer",
  ].filter(Boolean);

  return (
    <Box
      component="section"
      id="top"
      aria-labelledby="amb-hero-title"
      sx={{ position: "relative", pt: { xs: 5, sm: 7, lg: 9 }, pb: { xs: 7, md: 10 }, scrollMarginTop: 72 }}
    >
      <Container maxWidth="lg" sx={{ minWidth: 0, position: "relative" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 0.95fr) minmax(0, 1.05fr)" },
            gap: { xs: 6, lg: 7 },
            alignItems: "center",
          }}
        >
          {/* ── The offer ─────────────────────────────────────────────── */}
          <Box sx={{ maxWidth: { xs: 660, lg: "none" }, minWidth: 0 }}>
            <Box
              component="p"
              sx={{
                m: 0, mb: 2.5,
                display: "inline-flex", alignItems: "center", gap: 1,
                px: 1.5, py: 0.6, borderRadius: 999,
                border: "1px solid", borderColor: "var(--amb-accent-line)",
                bgcolor: "var(--amb-accent-soft)",
                color: "primary.main", fontSize: 13.5, fontWeight: 700, letterSpacing: "0.02em",
              }}
            >
              <Box aria-hidden="true" component="span" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "primary.main" }} />
              PesaFii Ambassadors
            </Box>

            <Typography
              id="amb-hero-title"
              variant="h1"
              component="h1"
              sx={{
                color: "text.primary",
                fontWeight: 800,
                fontSize: { xs: "2.35rem", sm: "3rem", md: "3.4rem", lg: "3.6rem" },
                lineHeight: 1.04,
                letterSpacing: "-0.03em",
              }}
            >
              Refer networks.{" "}
              <Box component="span" sx={{ display: "block", color: "primary.main" }}>
                Earn as they grow.
              </Box>
            </Typography>

            <Typography
              variant="body1"
              sx={{ mt: 2.5, color: "text.secondary", fontSize: { xs: "1.05rem", md: "1.15rem" }, maxWidth: 580 }}
            >
              {rateText
                ? `Earn ${rateText} of PesaFii's qualifying net revenue from every hotspot business you refer.`
                : "Earn a share of PesaFii's qualifying net revenue from every hotspot business you refer."}{" "}
              Your commission keeps recurring while the client continues generating qualifying revenue.
            </Typography>

            <CtaGroup cta={cta} sx={{ mt: 3.5 }} />

            <Box
              component="ul"
              sx={{
                listStyle: "none", m: 0, p: 0, mt: 3.5,
                display: "flex", flexWrap: "wrap", columnGap: 2.5, rowGap: 1,
              }}
            >
              {facts.map((f) => (
                <Box
                  component="li"
                  key={f}
                  sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "text.secondary", fontSize: 14.5, fontWeight: 600 }}
                >
                  <CheckRoundedIcon aria-hidden="true" sx={{ fontSize: 18, color: "primary.main" }} />
                  {f}
                </Box>
              ))}
            </Box>
          </Box>

          {/* ── The journey ───────────────────────────────────────────── */}
          <ReferralJourney ratePct={ratePct} />
        </Box>
      </Container>
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// THE REFERRAL JOURNEY
// ═══════════════════════════════════════════════════════════════════════════

// Grid placement: a loop on sm+ (link → business, down to revenue, back to
// you), a straight column on phones.
const AREAS = ["a", "b", "c", "d"];

// Connector geometry per step, from the card it leaves. xs: always straight
// down. sm+: right, down, then left.
const CONNECTORS = [
  {
    top: { xs: "100%", sm: "50%" }, left: { xs: 30, sm: "100%" }, right: "auto",
    width: { xs: 2, sm: GAP.col }, height: { xs: GAP.stack, sm: 2 },
    origin: { xs: "top center", sm: "left center" },
  },
  {
    top: "100%", left: { xs: 30, sm: "50%" }, right: "auto",
    width: 2, height: { xs: GAP.stack, sm: GAP.row },
    origin: "top center",
  },
  {
    top: { xs: "100%", sm: "50%" }, left: { xs: 30, sm: "auto" }, right: { xs: "auto", sm: "100%" },
    width: { xs: 2, sm: GAP.col }, height: { xs: GAP.stack, sm: 2 },
    origin: { xs: "top center", sm: "right center" },
  },
];

// Background network, hand-placed (a random layout re-rolls per mount).
const NET_NODES = [
  [40, 60], [150, 30], [270, 84], [400, 40], [540, 92], [90, 190], [220, 168],
  [350, 214], [480, 178], [580, 250], [50, 330], [180, 298], [300, 360],
  [440, 322], [560, 400], [120, 420], [380, 430],
];
const NET_LINKS = [
  [0, 1], [1, 2], [2, 3], [3, 4], [0, 5], [2, 6], [3, 7], [4, 8], [5, 6], [6, 7],
  [7, 8], [8, 9], [5, 10], [6, 11], [7, 12], [8, 13], [9, 14], [10, 11], [11, 12],
  [12, 13], [13, 14], [10, 15], [12, 16], [15, 16],
];

function ReferralJourney({ ratePct }) {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const primary = theme.palette.primary.main;

  const root = useRef(null);
  const tlRef = useRef(null);
  const takeover = useRef(false);
  const animated = useRef(false);
  const [active, setActive] = useState(FINAL);
  const [canReplay, setCanReplay] = useState(false);

  const rateText = ratePct != null ? `${ratePct}%` : null;

  const { contextSafe } = useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add({ reduced: MQ.reduced, fine: PAGE_MQ.finePointer, always: MQ.always }, (ctx) => {
        // Reduced motion: the finished journey is already on screen.
        if (ctx.conditions.reduced) return undefined;

        const el = root.current;
        if (!el) return undefined;
        const q = (s) => gsap.utils.toArray(s, el);
        const fills = q("[data-journey='fill']");
        const slice = q("[data-journey='slice']");
        const value = q("[data-journey='value']");
        const sweep = q("[data-journey='sweep']");

        // ── The one intro cycle ────────────────────────────────────────────
        try {
          takeover.current = false;
          const step = (i) => () => { if (!takeover.current) setActive(i); };
          gsap.set(fills, { scale: 0 });
          gsap.set(slice, { scaleX: 0 });

          const tl = gsap.timeline({ delay: 0.6, defaults: { ease: EASE.out } });
          tl.to(fills[0], { scale: 1, duration: 0.6, ease: EASE.inOut })
            .call(step(1))
            .to(fills[1], { scale: 1, duration: 0.6, ease: EASE.inOut }, "+=0.5")
            .call(step(2))
            .to(slice, { scaleX: 1, duration: 0.7 }, "+=0.1")
            .to(fills[2], { scale: 1, duration: 0.6, ease: EASE.inOut }, "+=0.15")
            .call(step(3))
            .fromTo(value, { scale: 0.88 }, { scale: 1, duration: DUR.slow, ease: "back.out(2)" })
            .fromTo(sweep, { xPercent: 0 }, { xPercent: 340, duration: 1.1, ease: EASE.inOut }, "<");
          tlRef.current = tl;

          // Before paint (useGSAP is a layout effect), so the finished state
          // never flashes before the cycle starts.
          setActive(0);
          animated.current = true;
          setCanReplay(true);
          el.setAttribute("data-motion", "ready");
        } catch (err) {
          gsap.set([...fills, ...slice, ...value, ...sweep], { clearProps: "transform" });
          tlRef.current = null;
          setActive(FINAL);
          console.error("[ambassador/landing] journey animation disabled", err);
          return undefined;
        }

        // ── Ambient, after the cycle ───────────────────────────────────────
        let stopAmbient = () => {};
        const drift = el.querySelector("[data-hero-net='drift']");
        const follow = el.querySelector("[data-hero-net='follow']");
        if (drift && !prefersLightweightMotion()) {
          const ambient = gsap.to(drift, {
            xPercent: 1.5, yPercent: -2.5, duration: 10, ease: "sine.inOut", repeat: -1, yoyo: true,
          });
          let onScreen = true;
          const sync = () => (document.hidden || !onScreen ? ambient.pause() : ambient.resume());
          const st = ScrollTrigger.create({
            trigger: el, start: "top bottom", end: "bottom top",
            onToggle: (self) => { onScreen = self.isActive; sync(); },
          });
          document.addEventListener("visibilitychange", sync);

          // Fine pointer only: the network leans a few pixels toward the cursor.
          let onMove = null;
          if (ctx.conditions.fine && follow) {
            const qx = gsap.quickTo(follow, "x", { duration: 1.2, ease: "power3.out" });
            const qy = gsap.quickTo(follow, "y", { duration: 1.2, ease: "power3.out" });
            onMove = (e) => {
              const r = el.getBoundingClientRect();
              if (!r.width || !r.height) return;
              qx(((e.clientX - r.left) / r.width - 0.5) * 16);
              qy(((e.clientY - r.top) / r.height - 0.5) * 12);
            };
            el.addEventListener("pointermove", onMove, { passive: true });
          }

          stopAmbient = () => {
            document.removeEventListener("visibilitychange", sync);
            if (onMove) el.removeEventListener("pointermove", onMove);
            st.kill();
          };
        }

        return () => {
          stopAmbient();
          el.removeAttribute("data-motion");
          tlRef.current = null;
          animated.current = false;
          setCanReplay(false);
          setActive(FINAL);
        };
      });
    },
    // Mount only. The rate arriving changes TEXT and the slice width, not the
    // choreography; rebuilding on it would replay the intro mid-read.
    { scope: root, dependencies: [] }
  );

  /** Walk the path to step i. Works with and without motion. */
  const select = contextSafe((i) => {
    takeover.current = true;
    const tl = tlRef.current;
    if (tl && tl.progress() < 1) tl.progress(1);
    setActive(i);
    const el = root.current;
    if (!el) return;
    const duration = animated.current ? 0.35 : 0;
    gsap.utils.toArray("[data-journey='fill']", el).forEach((f, j) => {
      gsap.to(f, { scale: j < i ? 1 : 0, duration, ease: EASE.out, overwrite: "auto" });
    });
    gsap.to(gsap.utils.toArray("[data-journey='slice']", el), {
      scaleX: i >= 2 ? 1 : 0, duration, ease: EASE.out, overwrite: "auto",
    });
  });

  const replay = contextSafe(() => {
    const tl = tlRef.current;
    const el = root.current;
    if (!tl || !el) return;
    takeover.current = false;
    gsap.set(gsap.utils.toArray("[data-journey='fill']", el), { scale: 0 });
    gsap.set(gsap.utils.toArray("[data-journey='slice']", el), { scaleX: 0 });
    setActive(0);
    tl.restart();
  });

  const nodes = [
    {
      icon: LinkRoundedIcon,
      title: "Your referral link",
      detail: (
        <Box component="span" sx={{ fontFamily: MONO, fontSize: 13, color: "text.primary", wordBreak: "break-all" }}>
          {SAMPLE_LINK}
        </Box>
      ),
    },
    { icon: StorefrontOutlinedIcon, title: "Business joins PesaFii", detail: "Signs up through your link" },
    {
      icon: InsightsOutlinedIcon,
      title: "Business generates qualifying revenue",
      detail: rateText ? `PesaFii earns fees. ${rateText} of that is yours.` : "PesaFii earns fees. Your share comes from them.",
      bar: true,
    },
    {
      icon: SavingsOutlinedIcon,
      title: "You earn",
      value: rateText,
      detail: "Recurring, while the business keeps generating qualifying revenue",
      earn: true,
    },
  ];

  const shadow = alpha(theme.palette.common.black, dark ? 0.45 : 0.1);

  return (
    <Box
      ref={root}
      data-stage={active}
      sx={{ position: "relative", isolation: "isolate", minWidth: 0 }}
    >
      {/* Background network: decoration only, behind the panel. */}
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: { xs: "-20px -12px", md: "-48px -40px" },
          zIndex: -1,
          pointerEvents: "none",
          opacity: { xs: 0.55, md: 0.9 },
        }}
      >
        <Box data-hero-net="drift" sx={{ position: "absolute", inset: 0 }}>
          <Box
            data-hero-net="follow"
            component="svg"
            viewBox="0 0 600 440"
            preserveAspectRatio="xMidYMid slice"
            sx={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
          >
            <g stroke={alpha(primary, dark ? 0.24 : 0.2)} strokeWidth="1" fill="none">
              {NET_LINKS.map(([a, b]) => (
                <line key={`${a}-${b}`} x1={NET_NODES[a][0]} y1={NET_NODES[a][1]} x2={NET_NODES[b][0]} y2={NET_NODES[b][1]} />
              ))}
            </g>
            <g fill={alpha(primary, dark ? 0.55 : 0.4)}>
              {NET_NODES.map(([cx, cy], i) => (
                <circle key={i} cx={cx} cy={cy} r={i % 4 === 0 ? 3.4 : 2.2} />
              ))}
            </g>
          </Box>
        </Box>
      </Box>

      {/* The panel. */}
      <Box
        sx={{
          borderRadius: 4,
          border: "1px solid",
          borderColor: "var(--amb-line)",
          bgcolor: "var(--amb-surface)",
          p: { xs: 2, sm: 3 },
          boxShadow: `0 30px 70px ${alpha(theme.palette.common.black, dark ? 0.5 : 0.12)}`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: { xs: 2, sm: 2.5 } }}>
          <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
            <Typography component="p" variant="overline" sx={{ color: "text.secondary", lineHeight: 1.2 }}>
              How a referral pays
            </Typography>
            <Box
              component="span"
              sx={{
                px: 1, py: 0.25, borderRadius: 1, fontSize: 11.5, fontWeight: 700,
                color: "text.secondary", border: "1px solid", borderColor: "var(--amb-line)",
              }}
            >
              Illustration
            </Box>
          </Stack>
          {canReplay && (
            <Button
              size="small"
              onClick={replay}
              startIcon={<ReplayRoundedIcon sx={{ fontSize: 18 }} />}
              sx={{ color: "text.secondary", minHeight: 36, borderRadius: 2, "&:hover": { color: "primary.main" } }}
            >
              Replay
            </Button>
          )}
        </Stack>

        <Box
          component="ol"
          aria-label="How a referral turns into commission"
          sx={{
            listStyle: "none", m: 0, p: 0,
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
            gridTemplateAreas: { xs: '"a" "b" "c" "d"', sm: '"a b" "d c"' },
            columnGap: { xs: 0, sm: `${GAP.col}px` },
            rowGap: { xs: `${GAP.stack}px`, sm: `${GAP.row}px` },
          }}
        >
          {nodes.map((n, i) => {
            const on = i === active;
            const passed = i < active;
            const Icon = n.icon;
            return (
              <Box component="li" key={n.title} sx={{ gridArea: AREAS[i], position: "relative", display: "flex", minWidth: 0 }}>
                <Box
                  component="button"
                  type="button"
                  aria-pressed={on}
                  onClick={() => select(i)}
                  sx={{
                    appearance: "none", font: "inherit", textAlign: "left", cursor: "pointer",
                    width: "100%", m: 0, p: { xs: 1.75, sm: 2 },
                    display: "flex", flexDirection: "column", gap: 1.25,
                    position: "relative", overflow: "hidden",
                    borderRadius: 3, border: "1px solid",
                    borderColor: on ? alpha(primary, 0.65) : passed ? "var(--amb-accent-line)" : "var(--amb-line)",
                    bgcolor: "var(--amb-surface-strong)",
                    color: "text.primary",
                    boxShadow: on ? `0 0 0 4px ${alpha(primary, 0.14)}, 0 16px 34px ${shadow}` : "none",
                    transform: on ? "translateY(-2px)" : "none",
                    transition: "border-color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease",
                    "&:hover": { borderColor: alpha(primary, 0.5) },
                    "&:focus-visible": { outline: `2px solid ${primary}`, outlineOffset: 3 },
                    "@media (prefers-reduced-motion: reduce)": { transition: "none", transform: "none" },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.25} component="span">
                    <Box
                      component="span"
                      aria-hidden="true"
                      sx={{
                        width: 38, height: 38, borderRadius: 2.25, flexShrink: 0,
                        display: "grid", placeItems: "center",
                        bgcolor: on ? "primary.main" : "var(--amb-accent-soft)",
                        color: on ? "primary.contrastText" : "primary.main",
                        transition: "background-color 0.3s ease, color 0.3s ease",
                      }}
                    >
                      <Icon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box component="span" aria-hidden="true" sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
                      {`0${i + 1}`}
                    </Box>
                  </Stack>

                  <Box component="span" sx={{ display: "block", fontWeight: 700, fontSize: 15.5, lineHeight: 1.3, color: "text.primary" }}>
                    {n.title}
                    {n.earn && (
                      <>
                        {" "}
                        <Box
                          component="span"
                          data-journey="value"
                          sx={{
                            display: "inline-block", fontSize: { xs: 24, sm: 28 }, fontWeight: 800,
                            color: "primary.main", lineHeight: 1, letterSpacing: "-0.02em",
                            fontVariantNumeric: "tabular-nums", transformOrigin: "left bottom",
                          }}
                        >
                          {n.value || "your commission"}
                        </Box>
                      </>
                    )}
                  </Box>

                  <Box component="span" sx={{ display: "block", fontSize: 13.5, lineHeight: 1.45, color: "text.secondary" }}>
                    {n.detail}
                  </Box>

                  {n.bar && (
                    <Box component="span" aria-hidden="true" sx={{ display: "block", mt: 0.25 }}>
                      <Box
                        component="span"
                        sx={{
                          display: "block", position: "relative", height: 8, borderRadius: 4,
                          bgcolor: alpha(primary, dark ? 0.2 : 0.16), overflow: "hidden",
                        }}
                      >
                        <Box
                          component="span"
                          data-journey="slice"
                          sx={{
                            position: "absolute", left: 0, top: 0, bottom: 0,
                            width: `${ratePct ?? 0}%`, minWidth: ratePct ? 6 : 0,
                            bgcolor: "primary.main", borderRadius: 4, transformOrigin: "left center",
                          }}
                        />
                      </Box>
                    </Box>
                  )}

                  {n.earn && (
                    <Box
                      component="span"
                      aria-hidden="true"
                      data-journey="sweep"
                      sx={{
                        position: "absolute", top: 0, bottom: 0, left: "-45%", width: "40%",
                        pointerEvents: "none",
                        background: `linear-gradient(100deg, transparent 0%, ${alpha(theme.palette.common.white, dark ? 0.1 : 0.55)} 50%, transparent 100%)`,
                      }}
                    />
                  )}
                </Box>

                {i < CONNECTORS.length && <Connector geometry={CONNECTORS[i]} lit={i < active} />}
              </Box>
            );
          })}
        </Box>

        <Typography variant="caption" component="p" sx={{ mt: { xs: 2, sm: 2.5 }, color: "text.secondary" }}>
          Illustration. Tap a step to follow the path. Commission is worked out on each qualifying transaction.
        </Typography>
      </Box>
    </Box>
  );
}

/** The line between two journey cards: a faint track and a travelling fill. */
function Connector({ geometry, lit }) {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: "absolute",
        top: geometry.top, left: geometry.left, right: geometry.right,
        width: geometry.width, height: geometry.height,
        borderRadius: 2, overflow: "hidden",
        bgcolor: "var(--amb-track)",
        pointerEvents: "none",
      }}
    >
      <Box
        data-journey="fill"
        sx={{
          position: "absolute", inset: 0,
          transformOrigin: geometry.origin,
          background: `linear-gradient(90deg, ${alpha(primary, 0.55)}, ${primary})`,
          opacity: lit ? 1 : 0.9,
        }}
      />
    </Box>
  );
}
